import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Row, Col } from 'react-bootstrap';
import { CreatableDropdown } from '../../../../../../components/Select/CreatableDropdown';
import {
  ExtraFieldsArea,
  ExtraFieldsSupportedObject,
} from '../../../../components/ExtraFields';
import { ExtraFieldsFormId } from '../../../../components/ExtraFields/types';
import { OptionBase } from '../../../../../../components/Select/types';

export type DynamicExpenseFormValues = {
  description: string;
  date: Date;
  categoryId: string;
  merchantId?: string;
  projectId: string;
  expenseGroupId: string;
  dateTo?: Date | null;
  timeFrom?: string;
  timeTo?: string;
};

export type DynamicExpenseFormOptions = {
  categoryOptions: OptionBase[];
  projectOptions: OptionBase[];
  expenseGroupOptions: OptionBase[];
  merchantOptions?: OptionBase[];
};

export type DynamicExpenseFormProps = {
  onValueChange: (field: string, value: any) => void;
  displayFullDateTime: boolean;
  values: DynamicExpenseFormValues;
  options: DynamicExpenseFormOptions;
  onCreateMerchant?: (merchantName: string) => void;
  dateLabel?: string;
  dateValidationError?: string | null;
  categoryRequired?: boolean;
  merchantRequired?: boolean;
  showMerchant?: boolean;
};

export const DynamicExpenseForm: React.FC<
  React.PropsWithChildren<DynamicExpenseFormProps>
> = ({
  onValueChange,
  displayFullDateTime,
  values,
  options,
  onCreateMerchant,
  dateLabel,
  dateValidationError,
  categoryRequired = false,
  merchantRequired = false,
  showMerchant = true,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <DescriptionField
        value={values.description}
        onChange={(value) => onValueChange('description', value)}
      />

      {displayFullDateTime ? (
        <>
          {/* Per Diem: Date From | Date To */}
          <Row>
            <Col md={6}>
              <DateField
                value={values.date}
                onChange={(value) => onValueChange('fromDate', value)}
                label={dateLabel || t('expenses.fields.dateFromLabel')}
                validationError={dateValidationError}
              />
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('expenses.fields.dateToLabel')}</Form.Label>
                <Form.Control
                  type="date"
                  value={
                    values.dateTo && !isNaN(values.dateTo.getTime())
                      ? values.dateTo.toISOString().split('T')[0]
                      : ''
                  }
                  onChange={(e) => {
                    const dateValue = e.target.value;
                    if (dateValue && dateValue.trim() !== '') {
                      const date = new Date(dateValue);
                      onValueChange(
                        'toDate',
                        !isNaN(date.getTime()) ? date : null,
                      );
                    } else {
                      onValueChange('toDate', null);
                    }
                  }}
                  isInvalid={!!dateValidationError}
                />
                {dateValidationError && (
                  <Form.Control.Feedback type="invalid">
                    {dateValidationError}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('expenses.fields.timeFromLabel')}</Form.Label>
                <Form.Control
                  type="time"
                  value={values.timeFrom || ''}
                  onChange={(e) => onValueChange('fromTime', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('expenses.fields.timeToLabel')}</Form.Label>
                <Form.Control
                  type="time"
                  value={values.timeTo || ''}
                  onChange={(e) => onValueChange('toTime', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <CategoryField
                value={values.categoryId}
                onChange={(value) => onValueChange('categoryId', value)}
                options={options.categoryOptions}
                required={categoryRequired}
              />
            </Col>
            <Col md={6}>
              {showMerchant && options.merchantOptions && (
                <MerchantField
                  merchantId={values.merchantId || ''}
                  onMerchantChange={(value) =>
                    onValueChange('merchantId', value)
                  }
                  merchantOptions={options.merchantOptions}
                  onCreateMerchant={onCreateMerchant || (() => {})}
                  required={merchantRequired}
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <ProjectField
                value={values.projectId}
                onChange={(value) => onValueChange('projectId', value)}
                options={options.projectOptions}
              />
            </Col>
            <Col md={6}>
              <ExpenseGroupField
                value={values.expenseGroupId}
                onChange={(value) => onValueChange('expenseGroupId', value)}
                options={options.expenseGroupOptions}
              />
            </Col>
          </Row>
        </>
      ) : (
        <>
          <Row>
            <Col md={6}>
              <DateField
                value={values.date}
                onChange={(value) => onValueChange('expenseDate', value)}
                label={dateLabel}
                validationError={dateValidationError}
              />
            </Col>
            <Col md={6}>
              <CategoryField
                value={values.categoryId}
                onChange={(value) => onValueChange('categoryId', value)}
                options={options.categoryOptions}
                required={categoryRequired}
              />
            </Col>
          </Row>
          {showMerchant && options.merchantOptions ? (
            <>
              <Row>
                <Col md={6}>
                  <MerchantField
                    merchantId={values.merchantId || ''}
                    onMerchantChange={(value) =>
                      onValueChange('merchantId', value)
                    }
                    merchantOptions={options.merchantOptions}
                    onCreateMerchant={onCreateMerchant || (() => {})}
                    required={merchantRequired}
                  />
                </Col>
                <Col md={6}>
                  <ProjectField
                    value={values.projectId}
                    onChange={(value) => onValueChange('projectId', value)}
                    options={options.projectOptions}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <ExpenseGroupField
                    value={values.expenseGroupId}
                    onChange={(value) => onValueChange('expenseGroupId', value)}
                    options={options.expenseGroupOptions}
                  />
                </Col>
              </Row>
            </>
          ) : (
            <Row>
              <Col md={6}>
                <ProjectField
                  value={values.projectId}
                  onChange={(value) => onValueChange('projectId', value)}
                  options={options.projectOptions}
                />
              </Col>
              <Col md={6}>
                <ExpenseGroupField
                  value={values.expenseGroupId}
                  onChange={(value) => onValueChange('expenseGroupId', value)}
                  options={options.expenseGroupOptions}
                />
              </Col>
            </Row>
          )}
        </>
      )}
      <ExtraFieldsFormArea />
    </>
  );
};

type DescriptionFieldProps = {
  value: string;
  onChange: (value: string) => void;
};

export const DescriptionField: React.FC<
  React.PropsWithChildren<DescriptionFieldProps>
> = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <Row>
      <Col>
        <Form.Group className="mb-3">
          <Form.Label>
            {t('expenses.fields.descriptionLabel')}{' '}
            <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            className="fs-3"
            placeholder={t('expenses.fields.descriptionPlaceholder')}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </Form.Group>
      </Col>
    </Row>
  );
};

type DateFieldProps = {
  value: Date;
  onChange: (value: Date) => void;
  label?: string;
  validationError?: string | null;
};

export const DateField: React.FC<React.PropsWithChildren<DateFieldProps>> = ({
  value,
  onChange,
  label,
  validationError,
}) => {
  const { t } = useTranslation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateValue = e.target.value;
    if (dateValue && dateValue.trim() !== '') {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        onChange(date);
      }
    } else {
      onChange(new Date());
    }
  };

  const dateString =
    value && !isNaN(value.getTime()) ? value.toISOString().split('T')[0] : '';

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        {label || t('expenses.fields.dateLabel')}{' '}
        <span className="text-danger">*</span>
      </Form.Label>
      <Form.Control
        type="date"
        value={dateString}
        onChange={handleChange}
        required
        isInvalid={!!validationError}
      />
      {validationError && (
        <Form.Control.Feedback type="invalid">
          {validationError}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

type CategoryFieldProps = {
  value: string;
  onChange: (value: string) => void;
  options: OptionBase[];
  required?: boolean;
};

export const CategoryField: React.FC<
  React.PropsWithChildren<CategoryFieldProps>
> = ({ value, onChange, options, required = false }) => {
  const { t } = useTranslation();
  return (
    <Form.Group className="mb-3">
      <Form.Label>
        {t('expenses.fields.categoryLabel')}{' '}
        {required && <span className="text-danger">*</span>}
      </Form.Label>
      <Form.Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}>
        <option value="">{t('expenses.fields.categoryPlaceholder')}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  );
};

type ProjectFieldProps = {
  value: string;
  onChange: (value: string) => void;
  options: OptionBase[];
};

export const ProjectField: React.FC<
  React.PropsWithChildren<ProjectFieldProps>
> = ({ value, onChange, options }) => {
  const { t } = useTranslation();
  return (
    <Form.Group className="mb-3">
      <Form.Label>{t('expenses.fields.projectLabel')}</Form.Label>
      <Form.Select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('expenses.fields.projectPlaceholder')}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  );
};

type ExpenseGroupFieldProps = {
  value: string;
  onChange: (value: string) => void;
  options: OptionBase[];
};

export const ExpenseGroupField: React.FC<
  React.PropsWithChildren<ExpenseGroupFieldProps>
> = ({ value, onChange, options }) => {
  const { t } = useTranslation();
  return (
    <Form.Group className="mb-3">
      <Form.Label>{t('expenses.fields.expenseGroupLabel')}</Form.Label>
      <Form.Select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('expenses.fields.expenseGroupPlaceholder')}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  );
};

type MerchantFieldProps = {
  merchantId: string;
  onMerchantChange: (value: string) => void;
  merchantOptions: OptionBase[];
  onCreateMerchant: (merchantName: string) => void;
  required?: boolean;
};

export const MerchantField: React.FC<
  React.PropsWithChildren<MerchantFieldProps>
> = ({
  merchantId,
  onMerchantChange,
  merchantOptions,
  onCreateMerchant,
  required = false,
}) => {
  const { t } = useTranslation();

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        {t('expenses.fields.merchantLabel')}{' '}
        {required && <span className="text-danger">*</span>}
      </Form.Label>
      <CreatableDropdown
        placeholder={t('expenses.fields.merchantPlaceholder')}
        options={merchantOptions}
        onCreate={onCreateMerchant}
        value={merchantId || null}
        heightMode="large"
        onChange={(value: string | null) => onMerchantChange(value || '')}
      />
    </Form.Group>
  );
};

type ExtraFieldsFormAreaProps = {
  className?: string;
};

export const ExtraFieldsFormArea: React.FC<
  React.PropsWithChildren<ExtraFieldsFormAreaProps>
> = ({ className = 'mb-3' }) => {
  return (
    <div className={className}>
      <ExtraFieldsArea
        objectApiName={ExtraFieldsSupportedObject.Expense}
        formId={ExtraFieldsFormId.ExpensesDetails}
        hideRecordType={true}
      />
    </div>
  );
};
